/**
 * 都市商务模板 - 详情页
 *
 * 设计理念：通栏大图轮播 + 左文右栏
 * 与 sunset（左右分屏 60/40）完全不同的排版骨架：
 * - 顶部：通栏大图轮播（带左右切换与指示点）
 * - 标题区：大标题 + 元信息标签
 * - 主体：左栏详细描述 + 右栏发布者联系卡（sticky）
 * - 相关推荐：横向滚动卡片
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/templates/urban/components/Navbar";
import UrbanFooter from "@/templates/urban/components/Footer";
import { getListingDetail, getListings, type ListingDetail as ListingDetailType, type ListingItem } from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function UrbanListingDetail() {
  const { id } = useParams<{ id: string }>();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();

  const [listing, setListing] = useState<ListingDetailType | null>(null);
  const [related, setRelated] = useState<ListingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);
    window.scrollTo(0, 0);
    getListingDetail(id)
      .then((data) => {
        setListing(data);
        document.title = data?.title ? `${data.title} - 详情` : '详情';
        if (data?.category_id) {
          getListings({ category_id: data.category_id, page_size: 6 })
            .then(d => setRelated((d.list || []).filter(i => i.id !== id).slice(0, 5)))
            .catch(() => {});
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F6F8]">
        <Navbar />
        <div className="animate-pulse">
          <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
            <div className="h-9 bg-gray-200 rounded-lg w-2/3" />
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 h-80 bg-gray-200 rounded-xl" />
              <div className="lg:w-80 h-80 bg-gray-200 rounded-xl" />
            </div>
          </div>
        </div>
        <UrbanFooter />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#F4F6F8]">
        <Navbar />
        <div className="pt-28 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-[#1B3A5C]/5">
            <i className="ri-error-warning-line text-3xl text-[#1B3A5C]/40" />
          </div>
          <p className="text-gray-500 font-medium text-lg mb-2">信息不存在</p>
          <p className="text-gray-400 text-sm mb-6">该信息可能已被删除或下架</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white text-sm font-bold rounded-xl hover:from-[#E26A1B] hover:to-[#C95A12] transition-all shadow-md">
            <i className="ri-arrow-left-line" />返回首页
          </Link>
        </div>
        <UrbanFooter />
      </div>
    );
  }

  const images = listing.images || [];

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <Navbar />

      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* ======== 面包屑 ======== */}
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-5">
            <Link to="/" className="hover:text-[#1B3A5C] transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="hover:text-[#1B3A5C] transition-colors">分类</Link>
            {listing.category?.name && (
              <>
                <i className="ri-arrow-right-s-line" />
                <Link to={`/category/${listing.category_id}`} className="hover:text-[#1B3A5C] transition-colors">
                  {listing.category.name}
                </Link>
              </>
            )}
          </nav>

          {/* ======== 标题 + 元信息 ======== */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight mb-4">
              {listing.title}
            </h1>
            <div className="flex items-center gap-3 flex-wrap">
              {listing.category?.name && (
                <span className="text-xs px-3 py-1 bg-[#1B3A5C]/5 text-[#1B3A5C] rounded font-medium">
                  {listing.category.name}
                </span>
              )}
              {listing.is_pinned && (
                <span className="text-xs px-2.5 py-1 bg-[#F08A24] text-white rounded font-bold flex items-center gap-1">
                  <i className="ri-pushpin-line text-[10px]" />置顶
                </span>
              )}
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <i className="ri-time-line" />{formatDate(listing.created_at)}
              </span>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <i className="ri-eye-line" />{listing.view_count || 0} 次浏览
              </span>
              {listing.location && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-map-pin-line" />{listing.location}
                </span>
              )}
            </div>
          </div>

          {/* ======== 主体：左文右栏 ======== */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* ===== 左栏：内容 ===== */}
            <div className="lg:flex-1 min-w-0 space-y-6">
              {/* 操作按钮 */}
              <div className="flex items-center gap-2">
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200 text-gray-500 hover:text-[#1B3A5C] hover:border-[#1B3A5C]/30 hover:bg-white transition-all text-sm">
                  <i className="ri-share-forward-line" />分享
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200 text-gray-500 hover:text-[#E26A1B] hover:border-[#F08A24]/40 hover:bg-orange-50 transition-all text-sm">
                  <i className="ri-heart-line" />收藏
                </button>
              </div>

              {/* 内容描述 */}
              <div className="bg-white rounded-xl border border-gray-100 p-6 sm:p-8">
                <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#F08A24] rounded-sm inline-block" />
                  详细描述
                </h3>
                {listing.content ? (
                  <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap break-words [&_a]:text-[#1B3A5C] [&_strong]:text-gray-800 [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mt-5 [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1">
                    {listing.content}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">暂无详细描述</p>
                )}

                {/* 标签 */}
                {listing.tags && listing.tags.length > 0 && (
                  <div className="mt-6 pt-5 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2">
                      {listing.tags.map((tag: string, i: number) => (
                        <span key={i} className="text-xs px-3 py-1.5 bg-[#1B3A5C]/5 text-[#1B3A5C] rounded font-medium">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 详情图片：竖状排列展示帖子所有图片 */}
              {images.length > 0 && (
                <div className="flex flex-col gap-4">
                  {images.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt=""
                      className="w-full aspect-square object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => { setCurrentImage(i); setLightboxOpen(true); }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ===== 右栏：发布者联系（sticky）===== */}
            <div className="lg:w-80 shrink-0">
              <div className="lg:sticky lg:top-24 space-y-5">
                {/* 发布者信息卡片 */}
                <div className="bg-white rounded-xl border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1B3A5C] to-[#2C5A8C] flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-md">
                      {listing.contact_name ? listing.contact_name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800">{listing.contact_name || "匿名用户"}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{formatDate(listing.created_at)} 发布</div>
                    </div>
                  </div>

                  {/* 公司名称：仅当后台未开启"隐藏公司名称"时显示（siteConfig 加载完成后再渲染，避免配置未知时误显示） */}
                  {siteConfig && listing.company_name && !siteConfig.hide_company_name && (
                    <div className="mb-4 p-3 bg-[#1B3A5C]/5 rounded-lg">
                      <span className="text-xs text-gray-400">公司名称</span>
                      <p className="text-sm font-medium text-gray-700 mt-0.5">{listing.company_name}</p>
                    </div>
                  )}

                  {/* 联系方式列表 */}
                  <div className="space-y-2.5">
                    {listing.contact_phone && (
                      <a
                        href={`tel:${listing.contact_phone}`}
                        className="flex items-center gap-3 px-4 py-3 bg-[#F08A24]/5 rounded-lg hover:bg-[#F08A24]/10 transition-colors group"
                      >
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#F08A24] to-[#E26A1B] flex items-center justify-center shrink-0">
                          <i className="ri-phone-line text-white" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">联系电话</div>
                          <div className="text-sm font-semibold text-gray-800 group-hover:text-[#E26A1B] transition-colors">
                            {listing.contact_phone}
                          </div>
                        </div>
                      </a>
                    )}
                    {listing.contact_wechat && (
                      <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 rounded-lg">
                        <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0">
                          <i className="ri-wechat-line text-white" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">微信</div>
                          <div className="text-sm font-semibold text-gray-800">{listing.contact_wechat}</div>
                        </div>
                      </div>
                    )}
                    {listing.contact_email && (
                      <a
                        href={`mailto:${listing.contact_email}`}
                        className="flex items-center gap-3 px-4 py-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
                      >
                        <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
                          <i className="ri-mail-line text-white" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">电子邮箱</div>
                          <div className="text-sm font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                            {listing.contact_email}
                          </div>
                        </div>
                      </a>
                    )}
                    {!listing.contact_phone && !listing.contact_wechat && !listing.contact_email && (
                      <p className="text-gray-400 text-sm text-center py-3">暂未提供联系方式</p>
                    )}
                  </div>
                </div>

                {/* 快速操作 */}
                <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">快速操作</h4>
                  <Link
                    to="/publish"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white font-bold rounded-lg hover:from-[#E26A1B] hover:to-[#C95A12] transition-all text-sm shadow-md shadow-orange-500/20"
                  >
                    <i className="ri-add-line" />发布信息
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* ======== 相关推荐：横向滚动 ======== */}
          {related.length > 0 && (
            <section className="mt-12">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#F08A24] rounded-sm inline-block" />
                  相关推荐
                </h2>
              </div>

              <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/listing/${item.id}`}
                    className="shrink-0 w-48 sm:w-56 group"
                  >
                    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-[#1B3A5C]/20 transition-all">
                      <div className="aspect-square bg-[#1B3A5C]/5 overflow-hidden">
                        {item.images?.[0] ? (
                          <img src={item.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-image-line text-[#1B3A5C]/20 text-2xl" />
                          </div>
                        )}
                      </div>
                      <div className="p-3.5">
                        <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug group-hover:text-[#1B3A5C] transition-colors">
                          {item.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <span className="text-[11px] px-2 py-0.5 bg-[#1B3A5C]/5 text-[#1B3A5C] rounded">
                            {item.category?.name || "未分类"}
                          </span>
                          <span>{formatDate(item.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

        </div>
      </div>

      {/* ======== 图片灯箱 ======== */}
      {lightboxOpen && images.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setCurrentImage((currentImage - 1 + images.length) % images.length); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
          )}
          <img
            src={images[currentImage]}
            alt=""
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setCurrentImage((currentImage + 1) % images.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <i className="ri-arrow-right-s-line text-2xl" />
              </button>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setCurrentImage(i); }}
                    className={`w-2 h-2 rounded-full transition-all ${i === currentImage ? "bg-white w-6" : "bg-white/40 hover:bg-white/60"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ======== 移动端底部浮动联系按钮 ======== */}
      {listing.contact_phone && (
        <div className="fixed bottom-0 inset-x-0 md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 px-4 py-3 z-40">
          <div className="flex items-center gap-3">
            <a
              href={`tel:${listing.contact_phone}`}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white font-bold rounded-lg shadow-lg shadow-orange-500/30"
            >
              <i className="ri-phone-line text-lg" />拨打电话
            </a>
            {listing.contact_wechat && (
              <button className="shrink-0 w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                <i className="ri-wechat-line text-lg" />
              </button>
            )}
          </div>
        </div>
      )}

      <UrbanFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
